(() => {
  const selectors = {
    root: '[data-dd-reuse-upsell]',
    state: '[data-dd-reuse-cart-state]',
    source: '[data-dd-reuse-source]',
    card: '[data-dd-reuse-product]',
    variant: '[data-dd-reuse-variant]',
    add: '[data-dd-reuse-add]',
    price: '[data-dd-reuse-price]',
    status: '[data-dd-reuse-status]',
    orphanWarning: '[data-dd-reuse-orphan-warning]',
  };

  function selectedSource(root) {
    const select = root.querySelector(selectors.source);
    return select && select.options[select.selectedIndex];
  }

  function cartState() {
    const lines = [];
    let orphanCount = 0;

    document.querySelectorAll(selectors.state).forEach((node) => {
      try {
        const parsed = JSON.parse(node.textContent || '{}');
        if (Array.isArray(parsed.lines)) {
          parsed.lines.forEach((line) => lines.push(line));
        }
        if (Number(parsed.orphan_count) > orphanCount) {
          orphanCount = Number(parsed.orphan_count);
        }
      } catch (_error) {
        // Ignore malformed state and let normal cart behavior continue.
      }
    });

    return { lines, orphanCount };
  }

  function hasAddonForSource(productId, sourceKey) {
    if (!productId || !sourceKey) return false;
    return cartState().lines.some((line) => (
      line &&
      line.is_addon === true &&
      String(line.product_id || '') === String(productId) &&
      String(line.source || '') === String(sourceKey)
    ));
  }

  function orphanAddons() {
    const { lines, orphanCount } = cartState();
    if (orphanCount > 0) return orphanCount;
    const keys = new Set(lines.map((line) => String(line?.key || '')).filter(Boolean));
    return lines.filter((line) => (
      line &&
      line.is_addon === true &&
      line.source &&
      !keys.has(String(line.source))
    )).length;
  }

  function setCheckoutBlocked(blocked) {
    const checkoutButtons = document.querySelectorAll(
      'button[name="checkout"], input[name="checkout"], .cart__checkout-button, [data-shopify="payment-button"] button, .additional-checkout-buttons button'
    );

    checkoutButtons.forEach((button) => {
      if (blocked) {
        if (!button.dataset.ddReuseOriginalDisabled) {
          button.dataset.ddReuseOriginalDisabled = button.disabled ? 'true' : 'false';
        }
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.dataset.ddReuseCheckoutBlocked = 'true';
      } else if (button.dataset.ddReuseCheckoutBlocked === 'true') {
        button.disabled = button.dataset.ddReuseOriginalDisabled === 'true';
        button.removeAttribute('aria-disabled');
        delete button.dataset.ddReuseCheckoutBlocked;
        delete button.dataset.ddReuseOriginalDisabled;
      }
    });

    document.querySelectorAll('.cart__dynamic-checkout-buttons, .additional-checkout-buttons').forEach((container) => {
      if (blocked) {
        container.dataset.ddReuseHidden = 'true';
        container.hidden = true;
      } else if (container.dataset.ddReuseHidden === 'true') {
        container.hidden = false;
        delete container.dataset.ddReuseHidden;
      }
    });
  }

  function enforceCartGuard() {
    const blocked = orphanAddons() > 0 || document.querySelector(selectors.orphanWarning);
    setCheckoutBlocked(Boolean(blocked));
  }

  function propertiesFor(source) {
    const properties = {
      'Reuse artwork from': source.dataset.sourceTitle || 'Existing order artwork',
      '_Reuse source cart line': source.value,
      '_Reuse source product id': source.dataset.sourceProductId || '',
      '_Reuse artwork add-on': 'true',
    };

    if (source.dataset.referencePhoto) {
      properties['Reference Photo'] = source.dataset.referencePhoto;
    }
    if (source.dataset.aiPreview) {
      properties['AI Preview'] = source.dataset.aiPreview;
    }
    if (source.dataset.previewJobId) {
      properties['_Preview Job ID'] = source.dataset.previewJobId;
    }
    if (source.dataset.previewStatus) {
      properties['_Preview Status'] = source.dataset.previewStatus;
    }

    return properties;
  }

  function currentCartLineKeys(root) {
    const cartItems = root.closest('cart-items');
    if (!cartItems) return new Set(cartState().lines.map((line) => String(line?.key || '')).filter(Boolean));

    return new Set(
      Array.from(cartItems.querySelectorAll('.cart-item a[href*="/cart/change"]'))
        .map((link) => {
          try {
            return new URL(link.href, window.location.origin).searchParams.get('id');
          } catch (_error) {
            return null;
          }
        })
        .filter(Boolean),
    );
  }

  function updateCardStates(root) {
    const source = selectedSource(root);
    const sourceKey = source && source.value;

    root.querySelectorAll(selectors.card).forEach((card) => {
      const button = card.querySelector(selectors.add);
      if (!button) return;

      const isSoldOut = button.dataset.soldOut === 'true';
      const alreadyAdded = hasAddonForSource(card.dataset.ddReuseProductId, sourceKey);
      button.disabled = isSoldOut || alreadyAdded || !sourceKey;
      button.textContent = alreadyAdded
        ? 'Already added for this artwork'
        : (isSoldOut ? 'Sold out' : (button.dataset.defaultText || 'Add to cart'));
    });
  }

  function syncSourcesWithCart(root) {
    const select = root.querySelector(selectors.source);
    if (!select) return;

    const currentLineKeys = currentCartLineKeys(root);
    Array.from(select.options).forEach((option) => {
      if (!currentLineKeys.has(option.value)) {
        option.remove();
      }
    });

    root.hidden = select.options.length === 0;
    updateCardStates(root);
    enforceCartGuard();
  }

  function observeCartChanges(root) {
    const cartRows = root.closest('cart-items')?.querySelector('#main-cart-items');
    if (!cartRows) return;

    let syncTimer;
    new MutationObserver(() => {
      window.clearTimeout(syncTimer);
      syncTimer = window.setTimeout(() => syncSourcesWithCart(root), 100);
    }).observe(cartRows, { childList: true, subtree: true });
  }

  function init(root) {
    syncSourcesWithCart(root);
    observeCartChanges(root);

    root.addEventListener('change', (event) => {
      const variant = event.target.closest(selectors.variant);
      const sourceSelect = event.target.closest(selectors.source);

      if (sourceSelect) {
        updateCardStates(root);
        return;
      }

      if (!variant || variant.tagName !== 'SELECT') return;
      const card = variant.closest(selectors.card);
      const price = card && card.querySelector(selectors.price);
      const option = variant.options[variant.selectedIndex];
      if (price && option && option.dataset.price) {
        price.textContent = option.dataset.price;
      }
    });

    root.addEventListener('click', async (event) => {
      const button = event.target.closest(selectors.add);
      if (!button || button.disabled) return;

      const card = button.closest(selectors.card);
      const variant = card && card.querySelector(selectors.variant);
      const source = selectedSource(root);
      const status = card && card.querySelector(selectors.status);
      if (!variant || !source) return;

      const originalText = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding…';
      if (status) status.textContent = '';

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: Number(variant.value),
                quantity: 1,
                properties: propertiesFor(source),
              },
            ],
          }),
        });

        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.description || result.message || 'Unable to add this item');
        }

        button.textContent = 'Added';
        if (status) status.textContent = 'Added with your existing artwork reference.';
        window.setTimeout(() => window.location.assign('/cart'), 450);
      } catch (error) {
        button.disabled = false;
        button.textContent = originalText;
        if (status) status.textContent = error.message || 'Unable to add this item. Please try again.';
      }
    });
  }

  function boot() {
    document.querySelectorAll(selectors.root).forEach(init);
    enforceCartGuard();

    const cartContainer = document.querySelector('cart-drawer') || document.querySelector('cart-items');
    if (cartContainer) {
      let guardTimer;
      new MutationObserver(() => {
        window.clearTimeout(guardTimer);
        guardTimer = window.setTimeout(enforceCartGuard, 120);
      }).observe(cartContainer, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
